import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelection } from "@/contexts/ActiveCompanyContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { FileSpreadsheet, Upload, CheckCircle2, AlertTriangle, Lock, ShieldAlert, Hand, Import as ImportIcon } from "lucide-react";
import { MONTHS_IT, fmtHours } from "@/lib/constants";
import { SuiteCard, SuiteButton, SuiteEyebrow } from "@/components/suite";
import { sortCompaniesIt } from "@/lib/sortCompanies";
import { useEffectiveRole } from "@/foundation/context";

const CLASS_LABELS = {
  new: { label: "Nuovi", color: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  unchanged: { label: "Già presenti", color: "bg-slate-50 text-slate-700 border-slate-200", icon: CheckCircle2 },
  conflict: { label: "Da verificare", color: "bg-amber-50 text-amber-800 border-amber-200", icon: AlertTriangle },
  protected_manual: { label: "Protetti (manuale)", color: "bg-violet-50 text-violet-800 border-violet-200", icon: Hand },
  protected_approved: { label: "Protetti (approvati)", color: "bg-sky-50 text-sky-800 border-sky-200", icon: ShieldAlert },
  protected_closed: { label: "Protetti (mese chiuso)", color: "bg-red-50 text-red-800 border-red-200", icon: Lock },
  no_hours_imported: { label: "Nessuna ora (saltati)", color: "bg-slate-50 text-slate-500 border-slate-200", icon: CheckCircle2 },
};

export default function ImportCartellino() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useCompanySelection();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!["admin", "super_admin"].includes(effectiveRole)) return;
    (async () => {
      try {
        const { data } = await api.get("/companies");
        const sorted = sortCompaniesIt(data);
        setCompanies(sorted);
        if (!companyId && sorted.length) setCompanyId(sorted[0].id);
      } catch (e) { toast.error(apiErr(e)); }
    })();
    /* eslint-disable-next-line */
  }, []);

  const analyze = async () => {
    if (!file || !companyId) { toast.error("Seleziona file e azienda"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/legacy-imports/cartellino/preview", fd, {
        params: { company_id: companyId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setSheets(data.sheets.map((s) => ({ ...s, import_this: !!s.existing_employee_id })));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const setDecision = (sheetIdx, dayIdx, decision) => {
    setSheets((prev) => prev.map((s, i) => {
      if (i !== sheetIdx) return s;
      const days = s.days.map((d, j) => j === dayIdx ? { ...d, decision } : d);
      return { ...s, days };
    }));
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/legacy-imports/cartellino/confirm", {
        company_id: companyId,
        sheets: sheets,
        status: "sent",
      });
      toast.success(
        `Import completato: +${data.inserted} nuovi, ${data.updated_ordinary} aggiornati, ` +
        `${data.kept_existing} mantenuti, ${data.skipped_manual + data.skipped_approved + data.skipped_closed} protetti`
      );
      setPreview(null); setSheets([]); setFile(null);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setConfirming(false); }
  };

  // Aggregate summary across all sheets
  const globalSummary = sheets.reduce((acc, s) => {
    Object.entries(s.classification_summary || {}).forEach(([k, v]) => {
      acc[k] = (acc[k] || 0) + v;
    });
    return acc;
  }, {});

  return (
    <div className="space-y-6 max-w-[1600px]">
      <div>
        <SuiteEyebrow icon={ImportIcon}>Import dedicato — Cartellino</SuiteEyebrow>
        <h1 className="text-3xl font-bold tracking-tight mt-1 text-suite-neutral-800" style={{ fontFamily: "Manrope" }}>
          Import Cartellino EASYPRE
        </h1>
        <p className="text-sm text-suite-neutral-500 mt-1">
          Caricamento settimanale progressivo: le presenze già approvate, le eccezioni inserite a mano e i mesi chiusi
          sono <b>protetti</b>. Solo le ore ordinarie non ancora validate vengono aggiornate.
        </p>
      </div>

      {!preview && (
        <SuiteCard padding="lg" testid="cart-upload-card">
          <div className="space-y-5">
            {["admin", "super_admin"].includes(effectiveRole) && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-suite-neutral-500 block mb-1">Azienda</Label>
                <Select value={companyId || ""} onValueChange={setCompanyId}>
                  <SelectTrigger className="w-72"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="border-2 border-dashed border-suite-neutral-300 rounded-md p-8 text-center bg-suite-neutral-50 hover:bg-suite-neutral-100 cursor-pointer"
                 onClick={() => document.getElementById("cart-input").click()}
                 data-testid="cart-dropzone">
              <FileSpreadsheet size={40} className="mx-auto text-suite-primary mb-2" />
              <div className="text-sm font-semibold text-suite-neutral-800">
                {file ? file.name : "Trascina 'CARTELLINO PRESENZE mese anno.xlsx' o clicca per selezionarlo"}
              </div>
              <input id="cart-input" type="file" accept=".xlsx" className="hidden"
                     onChange={(e) => setFile(e.target.files?.[0])} data-testid="cart-file-input" />
            </div>
            <div className="flex justify-end">
              <SuiteButton onClick={analyze} disabled={!file || !companyId || loading}
                      icon={Upload} loading={loading} data-testid="cart-analyze-btn">
                {loading ? "Analisi…" : "Analizza cartellino"}
              </SuiteButton>
            </div>
          </div>
        </SuiteCard>
      )}

      {preview && (
        <>
          {/* Riepilogo globale */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {Object.entries(CLASS_LABELS).map(([k, cfg]) => {
              const Icon = cfg.icon;
              const n = globalSummary[k] || 0;
              return (
                <div key={k} className={`border rounded-md p-3 ${cfg.color}`}>
                  <div className="flex items-center gap-1 text-xs uppercase tracking-widest">
                    <Icon size={12} /> {cfg.label}
                  </div>
                  <div className="text-2xl font-mono font-bold">{n}</div>
                </div>
              );
            })}
          </div>

          {/* Conflitti da risolvere - tabella dedicata */}
          {(globalSummary.conflict || 0) > 0 && (
            <SuiteCard padding="md" className="border-suite-warning-border" testid="cart-conflicts-card">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={16} className="text-suite-warning" />
                  <div className="text-sm font-bold text-suite-warning" style={{ fontFamily: "Manrope" }}>
                    Conflitti da risolvere ({globalSummary.conflict})
                  </div>
                </div>
                <p className="text-xs text-suite-neutral-600 mb-3">
                  Per ogni giorno con valore diverso, scegli se <b>mantenere</b> quello già inserito o <b>aggiornarlo</b> con quello del nuovo cartellino.
                </p>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs uppercase tracking-widest">Dipendente</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest">Data</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-right">Valore esistente</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-right">Nuovo cartellino</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-right">Δ</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest">Scelta</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sheets.flatMap((s, si) => s.days.map((d, di) => ({ ...d, si, di, name: s.existing_name })).filter((d) => d.classification === "conflict")).map((d) => (
                        <TableRow key={`${d.si}-${d.di}`}>
                          <TableCell className="text-sm">{d.name}</TableCell>
                          <TableCell className="font-mono text-sm">{d.date.slice(8,10)}/{d.date.slice(5,7)}</TableCell>
                          <TableCell className="text-right font-mono">{fmtHours(d.existing_hours)} h</TableCell>
                          <TableCell className="text-right font-mono">{fmtHours(d.hours_decimal)} h</TableCell>
                          <TableCell className={`text-right font-mono ${d.diff_hours >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                            {d.diff_hours > 0 ? "+" : ""}{fmtHours(d.diff_hours)}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" variant={d.decision === "keep" ? "default" : "outline"}
                                      onClick={() => setDecision(d.si, d.di, "keep")}
                                      className={d.decision === "keep" ? "bg-slate-700 text-white h-7 px-2 text-xs" : "h-7 px-2 text-xs"}
                                      data-testid={`conflict-keep-${d.si}-${d.di}`}>
                                Mantieni
                              </Button>
                              <Button size="sm" variant={d.decision === "overwrite" ? "default" : "outline"}
                                      onClick={() => setDecision(d.si, d.di, "overwrite")}
                                      className={d.decision === "overwrite" ? "bg-emerald-800 text-white h-7 px-2 text-xs" : "h-7 px-2 text-xs"}
                                      data-testid={`conflict-overwrite-${d.si}-${d.di}`}>
                                Aggiorna
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
            </SuiteCard>
          )}

          {/* Riepilogo per dipendente */}
          <SuiteCard padding="none" className="overflow-x-auto" testid="cart-summary-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Foglio</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Matricola</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Dipendente (app)</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Periodo</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Nuovi</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Uguali</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Conflitti</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Protetti</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Match</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sheets.map((s, si) => {
                    const cs = s.classification_summary || {};
                    const prot = (cs.protected_manual || 0) + (cs.protected_approved || 0) + (cs.protected_closed || 0);
                    return (
                      <TableRow key={si} className={s.existing_employee_id ? "" : "bg-red-50/40"}>
                        <TableCell>
                          <Checkbox checked={s.import_this && !!s.existing_employee_id}
                                    disabled={!s.existing_employee_id}
                                    onCheckedChange={(v) => setSheets((prev) => prev.map((x, i) => i === si ? { ...x, import_this: !!v } : x))} />
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">{s.sheet_name}</TableCell>
                        <TableCell className="font-mono">{s.matricola || "—"}</TableCell>
                        <TableCell className="text-sm font-semibold">
                          {s.existing_name || <span className="text-red-700">Non trovato</span>}
                          {s.month_locked && <Badge className="ml-2 bg-red-100 text-red-800">mese chiuso</Badge>}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{s.month ? `${MONTHS_IT[s.month - 1]} ${s.year}` : "—"}</TableCell>
                        <TableCell className="text-right font-mono text-emerald-800">{cs.new || 0}</TableCell>
                        <TableCell className="text-right font-mono text-slate-500">{cs.unchanged || 0}</TableCell>
                        <TableCell className="text-right font-mono text-amber-800 font-semibold">{cs.conflict || 0}</TableCell>
                        <TableCell className="text-right font-mono text-violet-800">{prot}</TableCell>
                        <TableCell>
                          {s.existing_employee_id
                            ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">ok</Badge>
                            : <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200">matr. mancante</Badge>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
          </SuiteCard>

          <div className="flex justify-end gap-2">
            <SuiteButton variant="secondary" onClick={() => { setPreview(null); setSheets([]); }}>Annulla</SuiteButton>
            <SuiteButton onClick={confirm} disabled={confirming}
                    icon={CheckCircle2} loading={confirming} data-testid="cart-confirm-btn">
              {confirming ? "Import in corso…" : `Conferma import (${sheets.filter((s) => s.import_this && s.existing_employee_id).length} dipendenti)`}
            </SuiteButton>
          </div>
        </>
      )}
    </div>
  );
}

